/**
 * Error handler utilities for authentication operations
 */

export type AuthErrorType = 
  | 'invalid_credentials' 
  | 'user_not_found' 
  | 'email_exists' 
  | 'username_exists'
  | 'weak_password'
  | 'invalid_email'
  | 'network_error'
  | 'server_error'
  | 'validation_error'
  | 'unknown_error';

export interface ParsedError {
  type: AuthErrorType;
  message: string;
  fieldErrors?: Record<string, string>;
}

/**
 * Parse and categorize API errors with user-friendly messages
 */
export function parseAuthError(error: unknown): ParsedError {
  // Handle network errors
  if (error instanceof Error && error.message.includes('Network')) {
    return {
      type: 'network_error',
      message: 'Network error. Please check your connection and try again.',
    };
  }

  // Handle axios errors
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as any;
    const status = axiosError.response?.status;
    const data = axiosError.response?.data;
    
    // Handle specific HTTP status codes
    if (status === 401) {
      return {
        type: 'invalid_credentials',
        message: 'Invalid username, email, or password. Please try again.',
      };
    }

    if (status === 400) {
      // Check for specific field errors
      if (typeof data === 'object' && data !== null) {
        const fieldErrors: Record<string, string> = {};
        
        // Handle different error response formats
        if (data.email && typeof data.email === 'object' && Array.isArray(data.email)) {
          fieldErrors.email = data.email[0]?.message || data.email[0] || 'Invalid email';
        }
        if (data.username && typeof data.username === 'object' && Array.isArray(data.username)) {
          fieldErrors.username = data.username[0]?.message || data.username[0] || 'Invalid username';
        }
        if (data.password && typeof data.password === 'object' && Array.isArray(data.password)) {
          fieldErrors.password = data.password[0]?.message || data.password[0] || 'Invalid password';
        }
        
        // Handle flat error format
        if (data.detail) {
          const detailMessage =
            typeof data.detail === 'string'
              ? data.detail
              : typeof data.detail === 'object'
              ? JSON.stringify(data.detail)
              : String(data.detail);
          return {
            type: 'validation_error',
            message: detailMessage,
            fieldErrors,
          };
        }

        if (Object.keys(fieldErrors).length > 0) {
          return {
            type: 'validation_error',
            message: 'Please correct the errors below.',
            fieldErrors,
          };
        }
      }

      // Check for common validation error messages
      const errorMsg =
        typeof data?.detail === 'string'
          ? data.detail
          : typeof data?.error === 'string'
          ? data.error
          : typeof data?.message === 'string'
          ? data.message
          : typeof data?.detail === 'object'
          ? JSON.stringify(data.detail)
          : typeof data?.error === 'object'
          ? JSON.stringify(data.error)
          : typeof data?.message === 'object'
          ? JSON.stringify(data.message)
          : '';
      if (typeof errorMsg === 'string') {
        if (errorMsg.toLowerCase().includes('email') && errorMsg.toLowerCase().includes('exists')) {
          return {
            type: 'email_exists',
            message: 'This email is already registered. Please log in or use a different email.',
          };
        }
        if (errorMsg.toLowerCase().includes('username') && errorMsg.toLowerCase().includes('exists')) {
          return {
            type: 'username_exists',
            message: 'This username is already taken. Please choose a different one.',
          };
        }
        if (errorMsg.toLowerCase().includes('password')) {
          return {
            type: 'weak_password',
            message: 'Password is too weak. Use at least 8 characters with mix of letters, numbers, and symbols.',
          };
        }
      }

      return {
        type: 'validation_error',
        message: 'Invalid input. Please check your information and try again.',
      };
    }

    if (status === 409) {
      const errorMsg = data?.detail || data?.error || data?.message || '';
      if (typeof errorMsg === 'string' && errorMsg.toLowerCase().includes('email')) {
        return {
          type: 'email_exists',
          message: 'This email is already registered. Please log in or use a different email.',
        };
      }
      if (typeof errorMsg === 'string' && errorMsg.toLowerCase().includes('username')) {
        return {
          type: 'username_exists',
          message: 'This username is already taken. Please choose a different one.',
        };
      }
      return {
        type: 'validation_error',
        message: 'This account already exists.',
      };
    }

    if (status === 404) {
      return {
        type: 'user_not_found',
        message: 'User not found. Please check your credentials or create a new account.',
      };
    }

    if (status === 429) {
      return {
        type: 'server_error',
        message: 'Too many login attempts. Please wait a few minutes and try again.',
      };
    }

    if (status && status >= 500) {
      return {
        type: 'server_error',
        message: 'Server error. Please try again later.',
      };
    }
  }

  // Handle Error objects
  if (error instanceof Error) {
    const message = error.message;
    
    if (message.includes('Network')) {
      return {
        type: 'network_error',
        message: 'Network error. Please check your connection and try again.',
      };
    }
    
    if (message.includes('credentials')) {
      return {
        type: 'invalid_credentials',
        message: 'Invalid username, email, or password. Please try again.',
      };
    }

    if (message.includes('email') && message.includes('exists')) {
      return {
        type: 'email_exists',
        message: 'This email is already registered. Please log in or use a different email.',
      };
    }

    if (message.includes('username') && message.includes('exists')) {
      return {
        type: 'username_exists',
        message: 'This username is already taken. Please choose a different one.',
      };
    }

    return {
      type: 'unknown_error',
      message: message || 'An error occurred. Please try again.',
    };
  }

  return {
    type: 'unknown_error',
    message: 'An unexpected error occurred. Please try again.',
  };
}

/**
 * Validate email format
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate password strength
 */
export function validatePasswordStrength(password: string): {
  isValid: boolean;
  requirements: {
    minLength: boolean;
    hasUpperCase: boolean;
    hasLowerCase: boolean;
    hasNumber: boolean;
  };
} {
  return {
    isValid: password.length >= 6,
    requirements: {
      minLength: password.length >= 6,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
    },
  };
}

/**
 * Validate registration form fields
 */
export interface RegistrationValidation {
  isValid: boolean;
  errors: Record<string, string>;
}

export function validateRegistrationForm(data: {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
}): RegistrationValidation {
  const errors: Record<string, string> = {};

  if (!data.firstName.trim()) {
    errors.firstName = 'First name is required';
  }

  if (!data.lastName.trim()) {
    errors.lastName = 'Last name is required';
  }

  if (!data.username.trim()) {
    errors.username = 'Username is required';
  } else if (data.username.length < 3) {
    errors.username = 'Username must be at least 3 characters';
  }

  if (!data.email.trim()) {
    errors.email = 'Email is required';
  } else if (!validateEmail(data.email)) {
    errors.email = 'Please enter a valid email address';
  }

  if (!data.password.trim()) {
    errors.password = 'Password is required';
  } else if (data.password.length < 6) {
    errors.password = 'Password must be at least 6 characters';
  }

  if (data.password !== data.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}
